import React, { useState, useEffect, useRef } from 'react';
import Button from '../../../../components/ui/button';
import Textarea from '../../../../components/ui/Textarea';
import CitySearchInput from '../../../geocoding/CitySearchInput';

/**
 * Props for the StopLocationDialog component
 */
interface StopLocationDialogProps {
  /** Dialog mode: 'add' for adding new stop, 'replace' for replacing existing stop */
  mode: 'add' | 'replace';
  /** Dialog title to display */
  title: string;
  /** Callback when user confirms the location selection */
  onConfirm: (method: 'manual' | 'ai_assisted', city?: string, country?: string, instructions?: string) => void;
  /** Callback when user cancels the dialog */
  onCancel: () => void;
  /** Initial city value (for replace mode) */
  initialCity?: string;
  /** Initial country value (for replace mode) */
  initialCountry?: string;
}

/**
 * StopLocationDialog Component
 * 
 * A unified, reusable dialog for adding or replacing journey stops.
 * Supports two input methods:
 * 1. AI-assisted: Let Jody suggest a location based on optional instructions
 * 2. Manual: User manually enters city and country
 * 
 * Features:
 * - Keyboard navigation (Enter to submit, Escape to cancel)
 * - Focus management for accessibility
 * - Input validation
 * - Prevents background scrolling while open
 * 
 * @example
 * // Adding a new stop
 * <StopLocationDialog
 *   mode="add"
 *   title="Add New Stop"
 *   onConfirm={(method, city, country, instructions) => handleAdd(...)}
 *   onCancel={() => setDialogOpen(false)}
 * />
 * 
 * @example
 * // Replacing an existing stop
 * <StopLocationDialog
 *   mode="replace"
 *   title="Replace Stop"
 *   initialCity="Paris"
 *   initialCountry="France"
 *   onConfirm={(method, city, country, instructions) => handleReplace(...)}
 *   onCancel={() => setDialogOpen(false)}
 * />
 */
const StopLocationDialog: React.FC<StopLocationDialogProps> = ({
  mode,
  title,
  onConfirm,
  onCancel,
  initialCity = '',
  initialCountry = ''
}) => {
  const [method, setMethod] = useState<'manual' | 'ai_assisted'>('ai_assisted');
  const [city, setCity] = useState(initialCity);
  const [country, setCountry] = useState(initialCountry);
  const [instructions, setInstructions] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);
  const instructionsTextareaRef = useRef<HTMLTextAreaElement>(null);

  /**
   * Focuses the AI-assisted instructions when that method is active.
   * (Manual mode shows the CitySearchInput, which the user clicks to focus.)
   */
  useEffect(() => {
    if (method === 'ai_assisted' && instructionsTextareaRef.current) {
      instructionsTextareaRef.current.focus();
    }
  }, [method]);

  /**
   * Handles keyboard shortcuts for better UX
   * - Escape: Cancel dialog
   * - Enter: Submit if form is valid
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    } else if (e.key === 'Enter' && canSubmit()) {
      handleConfirm();
    }
  };

  /**
   * Prevents background scrolling while dialog is open
   * Restores scrolling on unmount
   */
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  /**
   * Validates form inputs based on selected method
   * - Manual: Both city and country must be at least 3 characters
   * - AI-assisted: Always valid (instructions are optional)
   */
  const canSubmit = () => {
    if (method === 'manual') {
      return city.trim().length >= 3 && country.trim().length >= 3;
    }
    return true; // AI-assisted mode always valid
  };

  /**
   * Handles form submission
   * Calls onConfirm with appropriate parameters based on method
   */
  const handleConfirm = () => {
    if (canSubmit()) {
      if (method === 'manual') {
        onConfirm('manual', city.trim(), country.trim());
      } else {
        onConfirm('ai_assisted', undefined, undefined, instructions.trim() || undefined);
      }
    }
  };

  // Determine button text based on mode
  const confirmButtonText = mode === 'add' ? 'Add Stop' : 'Replace Stop';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onCancel}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-labelledby="dialog-title"
        aria-modal="true"
        className="bg-white rounded-lg p-6 w-96 max-w-full mx-4"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Dialog Header */}
        <h3 id="dialog-title" className="text-lg font-medium mb-4">
          {title}
        </h3>

        {/* Dialog Content */}
        <div className="space-y-4">
          {/* Method Selection */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="ai_assisted"
                name="method"
                value="ai_assisted"
                checked={method === 'ai_assisted'}
                onChange={(e) => setMethod(e.target.value as 'manual' | 'ai_assisted')}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="ai_assisted" className="text-sm font-medium text-gray-700 cursor-pointer">
                Let Jody suggest
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="manual"
                name="method"
                value="manual"
                checked={method === 'manual'}
                onChange={(e) => setMethod(e.target.value as 'manual' | 'ai_assisted')}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="manual" className="text-sm font-medium text-gray-700 cursor-pointer">
                Choose manually
              </label>
            </div>
          </div>

          {/* AI-Assisted Instructions */}
          {method === 'ai_assisted' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Optional instructions for Jody
              </label>
              <Textarea
                ref={instructionsTextareaRef}
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="e.g., near mountains, has good artistic scene, etc"
                rows={3}
                className="w-full"
              />
            </div>
          )}

          {/* Manual City/Country Input */}
          {method === 'manual' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City <span className="text-red-500">*</span>
                </label>
                <CitySearchInput
                  initialLabel={city}
                  placeholder="Search for a city, e.g. Berlin"
                  onSelect={(picked) => {
                    setCity(picked ? picked.name : '');
                    setCountry(picked ? (picked.country ?? '') : '');
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country{' '}
                  <span className="text-xs font-normal text-gray-400">(auto-filled from city)</span>
                </label>
                <input
                  type="text"
                  value={country}
                  readOnly
                  tabIndex={-1}
                  placeholder="Pick a city above"
                  className="w-full px-3 py-2 border border-gray-200 bg-gray-50 text-gray-500 rounded-md cursor-not-allowed focus:outline-none"
                />
              </div>
            </div>
          )}

          {/* Dialog Actions */}
          <div className="flex justify-end space-x-2 pt-2">
            <Button
              variant="secondary"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={!canSubmit()}
            >
              {confirmButtonText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StopLocationDialog;

